import React from 'react';
import { View, Text, StyleSheet, ViewStyle, TextStyle } from 'react-native';
import { AppColors } from '../utility/AppColors';
import { AppFontStyle } from '../styles/AppFontStyle';

interface NotificationBadgeProps {
  count: number;
  isVisible?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
  maxCount?: number;
  showZero?: boolean;
  position?: 'topRight' | 'topLeft' | 'bottomRight' | 'bottomLeft';
  size?: 'small' | 'medium' | 'large';
  color?: string;
  textColor?: string;
}

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  count,
  isVisible = true,
  style,
  textStyle,
  maxCount = 99,
  showZero = false,
  position = 'topRight',
  size = 'medium',
  color = AppColors.red_EA3737,
  textColor = AppColors.white,
}) => {
  // Don't render if not visible or count is 0 (unless showZero is true)
  if (!isVisible || (count <= 0 && !showZero)) {
    return null;
  }

  const displayCount = count > maxCount ? `${maxCount}+` : count.toString();

  const sizeStyles = getSizeStyles(size);
  
  const positionStyles = getPositionStyles(position);

  return (
    <View
      style={[
        styles.container,
        sizeStyles.container,
        positionStyles,
        { backgroundColor: color },
        style,
      ]}
    >
      <Text
        style={[
          styles.text,
          sizeStyles.text,
          { color: textColor },
          textStyle,
        ]}
        numberOfLines={1}
        adjustsFontSizeToFit
      >
        {displayCount}
      </Text>
    </View>
  );
};

const getSizeStyles = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return {
        container: styles.smallContainer,
        text: styles.smallText,
      };
    case 'large':
      return {
        container: styles.largeContainer,
        text: styles.largeText,
      };
    case 'medium':
    default:
      return {
        container: styles.mediumContainer,
        text: styles.mediumText,
      };
  }
};

const getPositionStyles = (position: NotificationBadgeProps['position']) => {
  switch (position) {
    case 'topLeft':
      return styles.topLeft;
    case 'bottomRight':
      return styles.bottomRight;
    case 'bottomLeft':
      return styles.bottomLeft;
    case 'topRight':
    default:
      return styles.topRight;
  }
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    zIndex: 10,
    minWidth: 18,
    borderWidth: 1,
    borderColor: AppColors.white,
  },
  text: {
    fontWeight: 'bold',
    textAlign: 'center',
  },
  
  // Size variants
  smallContainer: {
    height: 16,
    minWidth: 16,
    borderRadius: 8,
  },
  smallText: {
    fontSize: 10,
    lineHeight: 12,
  },
  
  mediumContainer: {
    height: 19,
    minWidth: 18,
    borderRadius: 10,
  },
  mediumText: {
    fontSize: 12,
    lineHeight: 14,
  },
  
  largeContainer: {
    height: 22,
    minWidth: 22,
    borderRadius: 11,
  },
  largeText: {
    fontSize: 14,
    lineHeight: 16,
  },
  
  // Position variants
  topRight: {
    top: -8,
    right: -8,
  },
  topLeft: {
    top: -8,
    left: -8,
  },
  bottomRight: {
    bottom: -8,
    right: -8,
  },
  bottomLeft: {
    bottom: -8,
    left: -8,
  },
});

export default NotificationBadge;
